/* eslint-disable react/no-danger */
/* eslint-disable react/require-default-props */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@shared/v2/Button/Button";
import Modal from "@shared/v2/Modal/Modal";
import TextButton from "@shared/v2/TextButton";
import TextInput from "@shared/v2/TextInput/TextInput";
import { ArrowUpRightFromSquareSolidV6 } from "@shared/v2/Icomoon";
import maskApiKey from "@shared/maskApiKey";
import useCopyToClipboard from "@shared/hooks/useCopyToClipboard";
import HubSpotModal from "./HubSpotModal";
import ZapierWidgetModal from "./ZapierWidgetModal";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";

const IntegrationModal = ({ integration, onClose }) => {
  const currentUser = useCurrentUser();
  const [hasCopied, copy] = useCopyToClipboard(1000);
  const [thirdPartyModal, setThirdPartyModal] = useState(null);
  const showOwnerContact = Boolean(integration?.apiKey) && !currentUser.account.isOwner;

  useEffect(() => {
    const onZapierLinkClick = (e) => {
      if (e.target.id === "zapier-widget-link") {
        setThirdPartyModal("zapier");
      }
    };
    document.addEventListener("click", onZapierLinkClick);
    return () => document.removeEventListener("click", onZapierLinkClick);
  }, []);

  return (
    <>
      <Modal
        className="tw-flex tw-justify-center tw-items-center"
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[600px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
        show={!thirdPartyModal && Boolean(integration)}
        onHide={onClose}
      >
        {integration && (
          <>
            <Modal.Header
              title={
                <div className="tw-flex tw-gap-[12px] tw-items-center">
                  <div className="tw-w-[80px] tw-h-[80px] tw-flex tw-justify-center tw-items-center tw-p-[8px] tw-rounded-[8px] tw-bg-gray-5">
                    <img className="tw-max-w-[60px]" src={integration.imageUrl} alt={integration.headline} />
                  </div>
                  <h4 className="tw-flex tw-flex-col tw-m-0">
                    <span className="tw-text-28d">{integration.headline}</span>
                    <span
                      className="tw-text-14d"
                      dangerouslySetInnerHTML={{ __html: integration.subtitle }}
                    />
                  </h4>
                </div>
              }
              closeButton
            />
            <Modal.Body className="tw-max-h-[55vh] tw-overflow-y-auto tw-py-[16px] tw-flex tw-flex-col tw-gap-[16px]">
              {integration.screenshotUrl && (
                <img className="tw-max-w-full" src={integration.screenshotUrl} alt={integration.headline} />
              )}
              {showOwnerContact ? (
                <div className="tw-flex tw-flex-col tw-gap-[20px]">
                  <p className="tw-m-0">{integration.nonOwnerDescription}</p>
                  <p className="tw-m-0 tw-font-bold">
                    Contact your Account Owner about enabling this integration.
                  </p>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: integration.longDescription }} />
              )}
              {integration.urlText && (
                <>
                  <div>
                    Learn more at{" "}
                    <a href={integration.url} target="_blank" rel="noreferrer">
                      {integration.urlText}
                    </a>
                  </div>
                  <div className="tw-font-bold">
                    {integration.headline} is part of the Brivity Platform. If you are not a Brivity Platform
                    customer yet, call 866-295-3322 and sign up today.
                  </div>
                </>
              )}
              {integration.apiKey && !showOwnerContact && (
                <>
                  <span className="tw-text-16d tw-font-semibold">How to connect?</span>
                  <ol className="tw-m-0 tw-pl-[1em] tw-flex tw-flex-col tw-gap-[16px]">
                    <li>
                      <div className="tw-mb-[16px]">Copy the API Key below.</div>
                      <div className="tw-flex tw-items-end tw-gap-[4px]">
                        <TextInput
                          containerClassName="tw-flex-1"
                          label="Your Brivity API Key"
                          readOnly
                          value={maskApiKey(currentUser.account.apiKey)}
                        />
                        <Button
                          size="medium"
                          schema="secondary"
                          className={`tw-transition-all ${hasCopied ? "tw-opacity-50" : ""}`}
                          onClick={() => copy(currentUser.account.apiKey)}
                        >
                          {hasCopied ? "Copied" : "Copy"}
                        </Button>
                      </div>
                    </li>
                    {integration.steps?.map((step, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i} dangerouslySetInnerHTML={{ __html: step }} />
                    ))}
                  </ol>
                </>
              )}
            </Modal.Body>
            <Modal.Footer className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-[16px]">
              {integration.slug === "brivity-va" && (
                <>
                  <p className="tw-flex-1 tw-mb-0">Request a consultation for Brivity VA today!</p>
                  <Button
                    className="tw-whitespace-nowrap"
                    size="medium"
                    onClick={() => setThirdPartyModal("hubspot")}
                  >
                    Request Consultation
                  </Button>
                </>
              )}
              {["idx", "kwkly"].includes(integration.slug) && (
                <>
                  <p className="tw-flex-1 tw-mb-0">
                    Learn about the Brivity Platform in a 15 minute demo webinar
                  </p>
                  <Button
                    className="tw-whitespace-nowrap"
                    size="medium"
                    onClick={() => setThirdPartyModal("hubspot")}
                  >
                    Get a Demo
                  </Button>
                </>
              )}
              {integration.apiKey && (
                <>
                  <TextButton
                    size="medium"
                    className="tw-px-0 tw-flex tw-items-center tw-gap-[4px]"
                    onClick={() => window.open(integration.helpUrl, "_blank")}
                  >
                    Learn More <ArrowUpRightFromSquareSolidV6 />
                  </TextButton>
                  <Button className="tw-whitespace-nowrap" size="medium" onClick={onClose}>
                    Done
                  </Button>
                </>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>
      <HubSpotModal
        formId={integration?.hubspotFormId}
        onClose={() => {
          setThirdPartyModal(null);
          onClose(null);
        }}
        show={thirdPartyModal === "hubspot"}
      />
      <ZapierWidgetModal
        onClose={() => {
          setThirdPartyModal(null);
          onClose(null);
        }}
        show={thirdPartyModal === "zapier"}
      />
    </>
  );
};

IntegrationModal.propTypes = {
  integration: PropTypes.shape(),
  onClose: PropTypes.func.isRequired,
};

IntegrationModal.defaultProps = {
  integration: undefined,
};

export default IntegrationModal;
